import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import type { EChartsOption } from 'echarts';
import type { GolfCourseStatsDto, GolfHoleStatsDto, GolfRateDto, GolfStatsScope } from '@jordan/types';
import { Alert, Breadcrumb, Button, EChart, PageHeader, Select, Spinner } from '@jordan/ui';
import { AppChrome } from '../../../components/AppChrome';
import { ApiError, apiGet } from '../../../lib/api';
import { golfSidebar } from './golfSidebar';

const longDate = (ymd: string) =>
  new Date(`${ymd}T00:00:00`).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
const fmt1 = (n: number | null | undefined) => (n == null ? '—' : n.toFixed(1));
const fmt2 = (n: number | null | undefined) => (n == null ? '—' : n.toFixed(2));
const pct = (n: number | null | undefined, d = 0) => (n == null ? '—' : `${(n * 100).toFixed(d)}%`);
/** Strokes relative to par, signed ("E" at level). */
const toPar = (n: number | null | undefined, d = 0) =>
  n == null ? '—' : n === 0 ? 'E' : `${n > 0 ? '+' : ''}${n.toFixed(d)}`;
const rateHint = (r: GolfRateDto) => (r.opp > 0 ? `${r.hit} of ${r.opp}` : 'not recorded');

// Series colours. Greens-in-regulation and fairways are a blue/amber pair (separable for every kind
// of colour vision), and over/under par diverge red/blue from a neutral level-par midpoint.
const GIR_COLOR = '#0ea5e9';
const FIR_COLOR = '#f59e0b';
const OVER_PAR = '#ef4444';
const UNDER_PAR = '#0ea5e9';
const LEVEL_PAR = '#94a3b8';
// Score distribution, ordered best → worst. Labels stay short so the legend below the donut wraps
// tidily in a narrow card.
const DIST = [
  { key: 'eagles', label: 'Eagle+', color: '#0ea5e9' },
  { key: 'birdies', label: 'Birdie', color: '#10b981' },
  { key: 'pars', label: 'Par', color: '#64748b' },
  { key: 'bogeys', label: 'Bogey', color: '#f59e0b' },
  { key: 'doubles', label: 'Double', color: '#ef4444' },
  { key: 'others', label: 'Triple+', color: '#7f1d1d' },
] as const;

const AXIS_LABEL = { color: 'rgba(var(--body-color-rgb), .65)' };
const CHART_TEXT = { fontFamily: 'var(--body-font-family)', color: 'var(--body-color)' };
const SPLIT_LINE = { lineStyle: { color: 'var(--gray-200)', type: 'dashed' as const } };

/** Average score against par, hole by hole — the shape of where strokes go at a course. */
function scoringByHoleOption(holes: GolfHoleStatsDto[]): EChartsOption {
  return {
    textStyle: CHART_TEXT,
    grid: { left: 8, right: 16, top: 16, bottom: 24, containLabel: true },
    tooltip: {
      trigger: 'axis',
      formatter: (p: unknown) => {
        const h = holes[(p as { dataIndex: number }[])[0].dataIndex];
        return (
          `<b>Hole ${h.holeNumber}</b> · par ${h.par ?? '—'}<br/>` +
          `Average <b>${fmt2(h.avgScore)}</b> (${toPar(h.avgToPar, 2)})<br/>` +
          `Played ${h.played} · GIR ${pct(h.gir.percent)} · Fairway ${pct(h.fir.percent)}<br/>` +
          `Putts ${fmt2(h.avgPutts)} · Penalties ${h.penalties}`
        );
      },
    },
    xAxis: {
      type: 'category',
      data: holes.map((h) => String(h.holeNumber)),
      name: 'Hole',
      nameLocation: 'end',
      nameGap: 6,
      nameTextStyle: { color: 'rgba(var(--body-color-rgb), .5)' },
      axisLabel: AXIS_LABEL,
      axisTick: { show: false },
    },
    yAxis: {
      type: 'value',
      name: 'Strokes vs par',
      nameTextStyle: { color: 'rgba(var(--body-color-rgb), .55)', align: 'left' },
      axisLabel: { ...AXIS_LABEL, formatter: (v: number) => toPar(v, 1) },
      splitLine: SPLIT_LINE,
    },
    series: [
      {
        type: 'bar',
        name: 'Strokes vs par',
        barMaxWidth: 26,
        itemStyle: { borderRadius: [4, 4, 0, 0] },
        data: holes.map((h) => ({
          value: h.avgToPar,
          itemStyle: {
            color: h.avgToPar == null || h.avgToPar === 0 ? LEVEL_PAR : h.avgToPar > 0 ? OVER_PAR : UNDER_PAR,
            // Bars below the axis are rounded on their lower edge instead.
            borderRadius: (h.avgToPar ?? 0) < 0 ? [0, 0, 4, 4] : [4, 4, 0, 0],
          },
        })),
      },
    ],
  } as EChartsOption;
}

/** Greens in regulation and fairways hit, hole by hole — two rates on one percentage axis. */
function accuracyByHoleOption(holes: GolfHoleStatsDto[]): EChartsOption {
  const asPct = (r: GolfRateDto) => (r.percent == null ? null : Math.round(r.percent * 1000) / 10);
  return {
    textStyle: CHART_TEXT,
    grid: { left: 8, right: 16, top: 32, bottom: 24, containLabel: true },
    legend: { top: 0, textStyle: { color: 'var(--body-color)' } },
    tooltip: {
      trigger: 'axis',
      formatter: (p: unknown) => {
        const h = holes[(p as { dataIndex: number }[])[0].dataIndex];
        return (
          `<b>Hole ${h.holeNumber}</b> · par ${h.par ?? '—'}<br/>` +
          `Greens in regulation <b>${pct(h.gir.percent)}</b> (${rateHint(h.gir)})<br/>` +
          `Fairways hit <b>${pct(h.fir.percent)}</b> (${rateHint(h.fir)})`
        );
      },
    },
    xAxis: {
      type: 'category',
      data: holes.map((h) => String(h.holeNumber)),
      axisLabel: AXIS_LABEL,
      axisTick: { show: false },
    },
    yAxis: {
      type: 'value',
      max: 100,
      axisLabel: { ...AXIS_LABEL, formatter: (v: number) => `${v}%` },
      splitLine: SPLIT_LINE,
    },
    series: [
      {
        name: 'Greens in regulation',
        type: 'bar',
        barMaxWidth: 14,
        itemStyle: { color: GIR_COLOR, borderRadius: [4, 4, 0, 0] },
        data: holes.map((h) => asPct(h.gir)),
      },
      {
        name: 'Fairways hit',
        type: 'bar',
        barMaxWidth: 14,
        itemStyle: { color: FIR_COLOR, borderRadius: [4, 4, 0, 0] },
        data: holes.map((h) => asPct(h.fir)),
      },
    ],
  } as EChartsOption;
}

/** How the holes played out overall: birdies through triples, ordered best to worst. */
function distributionOption(holes: GolfHoleStatsDto[]): EChartsOption {
  const data = DIST.map((d) => ({
    name: d.label,
    value: holes.reduce((a, h) => a + h[d.key], 0),
    itemStyle: { color: d.color, borderColor: 'var(--card-bg)', borderWidth: 2 },
  })).filter((d) => d.value > 0);
  return {
    textStyle: CHART_TEXT,
    tooltip: { trigger: 'item', formatter: '{b}: {c} holes ({d}%)' },
    legend: { bottom: 0, itemWidth: 10, itemHeight: 10, textStyle: { color: 'var(--body-color)', fontSize: 11 } },
    series: [
      {
        type: 'pie',
        // Centred, with the legend below — a horizontally offset centre clips the donut in a narrow card.
        radius: ['54%', '74%'],
        center: ['50%', '42%'],
        avoidLabelOverlap: true,
        label: { show: false },
        data,
      },
    ],
  } as EChartsOption;
}

const StatCard = ({ label, value, hint, icon }: { label: string; value: string; hint?: string; icon: string }) => (
  <div className="col-md-3 col-6">
    <div className="card border-0 shadow-sm h-100">
      <div className="card-body">
        <div className="d-flex align-items-center text-muted mb-1">
          <i className={`${icon} me-2`} />
          {label}
        </div>
        <h3 className="fw-bold mb-0">{value}</h3>
        {hint && <div className="text-muted fs-sm">{hint}</div>}
      </div>
    </div>
  </div>
);

/** Hole-by-hole table; `sort` switches between card order and toughest-first. */
function HoleTable({ holes, sort }: { holes: GolfHoleStatsDto[]; sort: 'hole' | 'hard' }) {
  const rows = useMemo(
    () =>
      sort === 'hole'
        ? holes
        : [...holes].sort((a, b) => (b.avgToPar ?? -Infinity) - (a.avgToPar ?? -Infinity) || a.holeNumber - b.holeNumber),
    [holes, sort],
  );
  const sum = (f: (h: GolfHoleStatsDto) => number | null) => holes.reduce((a, h) => a + (f(h) ?? 0), 0);
  const totalRate = (f: (h: GolfHoleStatsDto) => GolfRateDto): GolfRateDto => {
    const hit = holes.reduce((a, h) => a + f(h).hit, 0);
    const opp = holes.reduce((a, h) => a + f(h).opp, 0);
    return { hit, opp, percent: opp > 0 ? hit / opp : null };
  };
  const totalGir = totalRate((h) => h.gir);
  const totalFir = totalRate((h) => h.fir);

  return (
    <div className="table-responsive">
      <table className="table table-hover align-middle mb-0 text-nowrap">
        <thead>
          <tr>
            <th>Hole</th>
            <th className="text-center">Par</th>
            <th className="text-center">Played</th>
            <th className="text-end">Avg</th>
            <th className="text-end">vs Par</th>
            <th className="text-center">Best</th>
            <th className="text-center">Worst</th>
            <th className="text-end">GIR</th>
            <th className="text-end">Fairway</th>
            <th className="text-end">Putts</th>
            <th className="text-end">Penalties</th>
            <th className="text-center">Birdie+</th>
            <th className="text-center">Par</th>
            <th className="text-center">Bogey</th>
            <th className="text-center">Dbl+</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((h) => (
            <tr key={h.holeNumber}>
              <td className="fw-semibold">{h.holeNumber}</td>
              <td className="text-center">{h.par ?? '—'}</td>
              <td className="text-center text-muted">{h.played}</td>
              <td className="text-end fw-semibold">{fmt2(h.avgScore)}</td>
              <td
                className={`text-end ${h.avgToPar == null || h.avgToPar === 0 ? '' : h.avgToPar > 0 ? 'text-danger' : 'text-success'}`}
              >
                {toPar(h.avgToPar, 2)}
              </td>
              <td className="text-center">{h.best ?? '—'}</td>
              <td className="text-center">{h.worst ?? '—'}</td>
              <td className="text-end" title={rateHint(h.gir)}>
                {pct(h.gir.percent)}
              </td>
              <td className="text-end" title={rateHint(h.fir)}>
                {pct(h.fir.percent)}
              </td>
              <td className="text-end">{fmt2(h.avgPutts)}</td>
              <td className="text-end">{h.penalties || '—'}</td>
              <td className="text-center">{h.eagles + h.birdies || '—'}</td>
              <td className="text-center">{h.pars || '—'}</td>
              <td className="text-center">{h.bogeys || '—'}</td>
              <td className="text-center">{h.doubles + h.others || '—'}</td>
            </tr>
          ))}
        </tbody>
        <tfoot className="table-light">
          <tr className="fw-semibold">
            <td>Total</td>
            <td className="text-center">{sum((h) => h.par) || '—'}</td>
            <td />
            <td className="text-end">{fmt2(sum((h) => h.avgScore))}</td>
            <td className="text-end">{toPar(sum((h) => h.avgToPar), 2)}</td>
            <td />
            <td />
            <td className="text-end" title={rateHint(totalGir)}>
              {pct(totalGir.percent)}
            </td>
            <td className="text-end" title={rateHint(totalFir)}>
              {pct(totalFir.percent)}
            </td>
            <td className="text-end">{fmt2(sum((h) => h.avgPutts))}</td>
            <td className="text-end">{sum((h) => h.penalties) || '—'}</td>
            <td className="text-center">{sum((h) => h.eagles + h.birdies) || '—'}</td>
            <td className="text-center">{sum((h) => h.pars) || '—'}</td>
            <td className="text-center">{sum((h) => h.bogeys) || '—'}</td>
            <td className="text-center">{sum((h) => h.doubles + h.others) || '—'}</td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

export function GolfStatsPage() {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const [data, setData] = useState<GolfCourseStatsDto | null>(null);
  const [loading, setLoading] = useState(true);
  // The user's picks. null means "whatever the server resolves" — the most recently played course,
  // and the configured default scope. Kept separate from the response so adopting what came back
  // can't trigger a second fetch.
  const [pickedCourse, setPickedCourse] = useState<string | null>(null);
  const [pickedScope, setPickedScope] = useState<GolfStatsScope | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [needsSignIn, setNeedsSignIn] = useState(false);
  const [holeSort, setHoleSort] = useState<'hole' | 'hard'>('hole');

  const load = useCallback(() => {
    setLoading(true);
    setErr(null);
    setNeedsSignIn(false);
    const params = new URLSearchParams();
    if (pickedCourse) params.set('course', pickedCourse);
    if (pickedScope) params.set('scope', pickedScope);
    const qs = params.toString();
    apiGet<GolfCourseStatsDto>(`/api/health/golf/golfcanada/stats${qs ? `?${qs}` : ''}`)
      .then(setData)
      .catch((e) => {
        if (e instanceof ApiError && e.status === 409) {
          setNeedsSignIn(true);
          setData(null);
        } else {
          setErr('Could not load the course statistics.');
        }
      })
      .finally(() => setLoading(false));
  }, [pickedCourse, pickedScope]);
  useEffect(load, [load]);

  // What the controls show: the pick when it still applies, otherwise what the server resolved —
  // switching scope can drop the picked course out of range, and the server falls back for us.
  const course =
    data && pickedCourse && data.courses.some((c) => c.course === pickedCourse) ? pickedCourse : data?.course ?? '';
  const scope = pickedScope ?? data?.scope ?? 'all';

  const summary = data?.summary ?? null;
  // Memoised so the chart options below aren't rebuilt on every render.
  const holes = useMemo(() => data?.holes ?? [], [data]);
  const scoring = useMemo(() => (holes.length ? scoringByHoleOption(holes) : null), [holes]);
  const accuracy = useMemo(() => (holes.length ? accuracyByHoleOption(holes) : null), [holes]);
  const distribution = useMemo(() => (holes.length ? distributionOption(holes) : null), [holes]);

  const seasonLabel = data ? `This season (since ${longDate(data.seasonStartDate)})` : 'This season';

  return (
    <AppChrome secondarySidebar={golfSidebar(navigate, pathname)}>
      <PageHeader
        title={
          <>
            Golf - <span className="fw-normal">Stats</span>
          </>
        }
        breadcrumb={<Breadcrumb items={[{ icon: 'ph-house', href: '/' }, { label: 'Golf' }, { label: 'Stats' }]} />}
      />

      <div className="content">
        {err && (
          <Alert variant="danger" icon="ph-warning" dismissible>
            {err}
          </Alert>
        )}

        {needsSignIn && (
          <Alert variant="warning" icon="ph-info">
            <div className="d-flex align-items-center flex-wrap gap-2">
              <span className="me-auto">
                Course statistics come from your Golf Canada scorecards, which need a signed-in Score Centre session.
              </span>
              <Button variant="light" size="sm" icon="ph-sign-in" onClick={() => navigate('/health/golf/scores')}>
                Go to Scores to sign in
              </Button>
            </div>
          </Alert>
        )}

        {/* Course + scope pickers. Kept mounted while a fetch is in flight so switching course
            doesn't collapse the page — reading a course's scorecards takes a moment. */}
        {data && data.courses.length > 0 && (
          <div className="card border-0 shadow-sm mb-3">
            <div className="card-body pb-0">
              <div className="row g-3">
                <div className="col-lg-6">
                  <Select
                    label="Course"
                    value={course}
                    onChange={(e) => setPickedCourse(e.target.value)}
                    disabled={loading}
                    options={data.courses.map((c) => ({
                      value: c.course,
                      label: `${c.course} · ${c.rounds} round${c.rounds === 1 ? '' : 's'}`,
                    }))}
                  />
                </div>
                <div className="col-lg-4">
                  <Select
                    label="Rounds included"
                    value={scope}
                    onChange={(e) => setPickedScope(e.target.value as GolfStatsScope)}
                    disabled={loading}
                    options={[
                      { value: 'all', label: 'All time' },
                      { value: 'season', label: seasonLabel },
                    ]}
                  />
                </div>
                {summary?.firstPlayed && summary.lastPlayed && (
                  <div className="col-lg-2">
                    <div className="form-label">Played</div>
                    <div className="text-muted fs-sm">
                      {summary.firstPlayed === summary.lastPlayed
                        ? longDate(summary.lastPlayed)
                        : `${longDate(summary.firstPlayed)} – ${longDate(summary.lastPlayed)}`}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {loading && <Spinner />}

        {!loading && data && data.courses.length === 0 && (
          <Alert variant="info" icon="ph-info">
            No rounds with a course name were found{data.scope === 'season' ? ' this season' : ''}. Golf Canada only
            records a course on rounds posted through the Score Centre.
          </Alert>
        )}

        {!loading && data && summary && (
          <>
            {/* Course overview */}
            <div className="row g-3 mb-3">
              <StatCard
                label="Rounds played"
                value={String(summary.rounds)}
                hint={`${summary.roundsEighteen} at 18 holes`}
                icon="ph-stack"
              />
              <StatCard
                label="Scoring average"
                value={fmt1(summary.scoringAverage)}
                hint="18 holes"
                icon="ph-chart-line"
              />
              <StatCard
                label="Best round"
                value={summary.best ? String(summary.best.score) : '—'}
                hint={summary.best ? longDate(summary.best.date) : undefined}
                icon="ph-trophy"
              />
              <StatCard label="Average vs par" value={toPar(summary.avgToPar, 1)} hint="18 holes" icon="ph-flag" />
            </div>

            {/* Tee to green */}
            <div className="row g-3 mb-3">
              <StatCard
                label="Greens in regulation"
                value={pct(summary.gir.percent)}
                hint={rateHint(summary.gir)}
                icon="ph-target"
              />
              <StatCard label="Fairways hit" value={pct(summary.fir.percent)} hint={rateHint(summary.fir)} icon="ph-path" />
              <StatCard
                label="Putts / round"
                value={fmt1(summary.puttsPerRound)}
                hint="18 holes"
                icon="ph-arrow-clockwise"
              />
              <StatCard
                label="Penalties / round"
                value={fmt1(summary.penaltiesPerRound)}
                hint="strokes"
                icon="ph-warning-octagon"
              />
            </div>

            {/* Recovery + scoring */}
            <div className="row g-3 mb-3">
              <StatCard
                label="Scrambling"
                value={pct(summary.scrambling.percent)}
                hint={rateHint(summary.scrambling)}
                icon="ph-arrows-clockwise"
              />
              <StatCard
                label="Sand saves"
                value={pct(summary.sandSave.percent)}
                hint={rateHint(summary.sandSave)}
                icon="ph-hourglass"
              />
              <StatCard
                label="Bounce back"
                value={pct(summary.bounceBack.percent)}
                hint={rateHint(summary.bounceBack)}
                icon="ph-pulse"
              />
              <StatCard label="Birdies / round" value={fmt1(summary.birdiesPerRound)} hint="or better" icon="ph-bird" />
            </div>

            {/* Handicap + par performance */}
            <div className="row g-3 mb-3">
              <StatCard label="Avg differential" value={fmt1(summary.avgDifferential)} icon="ph-function" />
              <StatCard
                label="Recent form"
                value={fmt1(summary.recentDifferential)}
                hint="avg diff, last 5 here"
                icon="ph-trend-up"
              />
              <div className="col-md-6">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-body">
                    <div className="d-flex align-items-center text-muted mb-2">
                      <i className="ph-golf me-2" />
                      Average by par
                    </div>
                    <div className="row g-2 text-center">
                      {[
                        { label: 'Par 3', value: summary.par3Avg, par: 3 },
                        { label: 'Par 4', value: summary.par4Avg, par: 4 },
                        { label: 'Par 5', value: summary.par5Avg, par: 5 },
                      ].map((p) => (
                        <div className="col" key={p.label}>
                          <div className="fs-3 fw-bold">{fmt2(p.value)}</div>
                          <div className="text-muted fs-sm">
                            {p.label}
                            {p.value != null && (
                              <span className={p.value > p.par ? ' text-danger' : ' text-success'}>
                                {' '}
                                {toPar(Math.round((p.value - p.par) * 100) / 100, 2)}
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {holes.length === 0 ? (
              <Alert variant="info" icon="ph-info">
                No hole-by-hole scorecards at {data.course}. Rounds posted as a total score carry no hole detail — the
                breakdown appears for rounds entered hole by hole in the Golf Canada Score Centre.
              </Alert>
            ) : (
              <>
                {/* Where the strokes go */}
                <div className="row g-3 mb-3">
                  <div className="col-xl-8">
                    <div className="card border-0 shadow-sm h-100">
                      <div className="card-header bg-transparent">
                        <h6 className="mb-0 fw-semibold">Average score against par, by hole</h6>
                      </div>
                      <div className="card-body">{scoring && <EChart option={scoring} height={300} />}</div>
                    </div>
                  </div>
                  <div className="col-xl-4">
                    <div className="card border-0 shadow-sm h-100">
                      <div className="card-header bg-transparent">
                        <h6 className="mb-0 fw-semibold">Hole outcomes</h6>
                      </div>
                      <div className="card-body">{distribution && <EChart option={distribution} height={300} />}</div>
                    </div>
                  </div>
                </div>

                {/* Accuracy */}
                <div className="card border-0 shadow-sm mb-3">
                  <div className="card-header bg-transparent">
                    <h6 className="mb-0 fw-semibold">Greens in regulation and fairways, by hole</h6>
                  </div>
                  <div className="card-body">{accuracy && <EChart option={accuracy} height={280} />}</div>
                </div>

                {/* Hole-by-hole detail */}
                <div className="card border-0 shadow-sm mb-3">
                  <div className="card-header bg-transparent d-flex align-items-center gap-2">
                    <h6 className="mb-0 fw-semibold me-auto">Hole by hole</h6>
                    <Select
                      selectSize="sm"
                      value={holeSort}
                      onChange={(e) => setHoleSort(e.target.value as 'hole' | 'hard')}
                      options={[
                        { value: 'hole', label: 'By hole' },
                        { value: 'hard', label: 'Toughest first' },
                      ]}
                      style={{ width: 'auto' }}
                      aria-label="Hole order"
                    />
                  </div>
                  <HoleTable holes={holes} sort={holeSort} />
                </div>
              </>
            )}

            {/* Rounds behind the numbers */}
            <div className="card border-0 shadow-sm">
              <div className="card-header bg-transparent">
                <h6 className="mb-0 fw-semibold">Rounds at {data.course}</h6>
              </div>
              <div className="table-responsive">
                <table className="table align-middle mb-0">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th className="text-center">Holes</th>
                      <th>Tees</th>
                      <th className="text-end">Score</th>
                      <th className="text-end">To Par</th>
                      <th className="text-end">Diff</th>
                      <th className="text-end">Putts</th>
                      <th className="text-end">GIR</th>
                      <th className="text-end">Fairway</th>
                      <th className="text-end">Penalties</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {data.rounds.map((r) => (
                      <tr key={r.id}>
                        <td>{r.date}</td>
                        <td className="text-center">{r.holes}</td>
                        <td>{r.tee ?? '—'}</td>
                        <td className="text-end fw-semibold">{r.score}</td>
                        <td className="text-end">{toPar(r.toPar)}</td>
                        <td className="text-end text-danger">{fmt1(r.differential)}</td>
                        <td className="text-end">{r.putts ?? '—'}</td>
                        <td className="text-end">{pct(r.girPercent)}</td>
                        <td className="text-end">{pct(r.firPercent)}</td>
                        <td className="text-end">{r.penalties ?? '—'}</td>
                        <td className="text-end">
                          <a
                            href={`/health/golf/scores/${r.id}`}
                            className="text-body-secondary"
                            title="View round details"
                            onClick={(e) => {
                              e.preventDefault();
                              navigate(`/health/golf/scores/${r.id}`);
                            }}
                          >
                            <i className="ph-chart-bar" />
                          </a>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Which rounds each figure is drawn from — the three counts differ, and it matters. */}
            <p className="text-muted fs-sm mt-3 mb-0">
              <i className="ph-info me-1" />
              Scores and differentials cover all {summary.rounds} round{summary.rounds === 1 ? '' : 's'} here.
              Hole-by-hole figures, putts and penalties come from the {summary.detailedRounds} round
              {summary.detailedRounds === 1 ? '' : 's'} posted hole by hole
              {summary.trackedRounds !== summary.detailedRounds && (
                <>
                  , and greens, fairways and recovery rates from the {summary.trackedRounds} that tracked statistics
                </>
              )}
              .{data.truncated && ' Only the most recent rounds were read in full (see Configuration → Health → Golf).'}
            </p>
          </>
        )}
      </div>
    </AppChrome>
  );
}
